/**
 * Royal Zanzibar Beach Resort — STO rates 2026/2027 (USD, AI, per person).
 * Source: "2026-2027 STO RZ" hotel contract. Rates are NET, non-commissionable,
 * inclusive of current government taxes. All rooms on request.
 *
 * Backend inventory.room_code is INTEGER. Belvedere uses 1–14.
 * Zanzibar uses 101–103 so catalog codes cannot collide with Belvedere seeds.
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const ROYAL_ZANZIBAR_CAMPAIGN_SLUG = "royal-zanzibar-beach-resort-2026";
export const ROYAL_ZANZIBAR_CURRENCY = "USD";
export const ROYAL_ZANZIBAR_BOARD = "AI";

/** Aliases accepted in URL / ops / legacy payloads. */
export const ROYAL_ZANZIBAR_CAMPAIGN_ALIASES = Object.freeze([
  ROYAL_ZANZIBAR_CAMPAIGN_SLUG,
  "royal-zanzibar-2026",
  "royal-zanzibar",
]);

export function isRoyalZanzibarCampaignCode(code) {
  const normalized = String(code || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  return ROYAL_ZANZIBAR_CAMPAIGN_ALIASES.some((alias) => alias === normalized);
}

/**
 * Royal Zanzibar STO: flights go to LDS as PROPOSAL only — never auto BOOK / Duffel.
 * @param {object} saveBody
 * @param {string} [hotelCode]
 * @param {string} [proposalOnlyKey="flights_module_proposal_only"]
 */
export function withRoyalZanzibarLdsProposalOnly(
  saveBody,
  hotelCode,
  proposalOnlyKey = "flights_module_proposal_only",
) {
  if (!saveBody || typeof saveBody !== "object") return saveBody;
  if (!isRoyalZanzibarCampaignCode(hotelCode)) return saveBody;
  const clientsInfo =
    saveBody.clientsInfo && typeof saveBody.clientsInfo === "object"
      ? {
          ...saveBody.clientsInfo,
          booking_state: "proposal_only",
          hrFlightPackage: {
            ...(saveBody.clientsInfo.hrFlightPackage || {}),
            proposal_only: true,
            source_module: "flights_new",
          },
        }
      : saveBody.clientsInfo;
  return {
    ...saveBody,
    [proposalOnlyKey]: true,
    clientsInfo,
    flights:
      saveBody.flights && typeof saveBody.flights === "object"
        ? { ...saveBody.flights, [proposalOnlyKey]: true }
        : saveBody.flights,
  };
}

/** Inclusive child ages as printed: "Children (2-12 yrs)". */
export const ROYAL_ZANZIBAR_CHILD_AGE_FROM = 2;
export const ROYAL_ZANZIBAR_CHILD_AGE_TO = 12;

export const ROYAL_ZANZIBAR_CHILD_SHARING_PERCENT = 50;
export const ROYAL_ZANZIBAR_CHILD_OWN_ROOM_PERCENT = 80;
export const ROYAL_ZANZIBAR_EXTRA_ADULT_PERCENT = 70;

/**
 * Numeric inventory codes (BE INTEGER) + semantic ids (FE SSOT).
 * Do not reuse Belvedere 1–14.
 */
export const ROYAL_ZANZIBAR_ROOM_CODES = Object.freeze({
  superior: 101,
  superiorDeluxe: 102,
  oceanFront: 103,
});

export const ROYAL_ZANZIBAR_ROOM_IDS = Object.freeze({
  superior: "superior-room",
  superiorDeluxe: "superior-deluxe-room",
  oceanFront: "ocean-front-suite",
});

export const ROYAL_ZANZIBAR_DELUXE_ADULT_SUPPLEMENT = 73;
export const ROYAL_ZANZIBAR_DELUXE_CHILD_SUPPLEMENT = 36.5;
export const ROYAL_ZANZIBAR_OCEAN_ADULT_SUPPLEMENT = 130;
export const ROYAL_ZANZIBAR_OCEAN_CHILD_SUPPLEMENT = 65;

const DOUBLE_PP = {
  S1: 218,
  S2: 520,
  S3: 297,
  S4: 164,
  S5: 206,
  S6: 297,
  S7: 218,
};

const SINGLE = {
  S1: 327,
  S2: 780,
  S3: 446,
  S4: 246,
  S5: 309,
  S6: 446,
  S7: 327,
};

export const ROYAL_ZANZIBAR_SEASONS = [
  { code: "S1", from: "2026-11-01", to: "2026-12-23", minStayNights: 1, double: DOUBLE_PP.S1, single: SINGLE.S1 },
  { code: "S2", from: "2026-12-24", to: "2027-01-05", minStayNights: 5, double: DOUBLE_PP.S2, single: SINGLE.S2 },
  { code: "S3", from: "2027-01-06", to: "2027-03-31", minStayNights: 1, double: DOUBLE_PP.S3, single: SINGLE.S3 },
  { code: "S4", from: "2027-04-01", to: "2027-05-31", minStayNights: 1, double: DOUBLE_PP.S4, single: SINGLE.S4 },
  { code: "S5", from: "2027-06-01", to: "2027-06-30", minStayNights: 1, double: DOUBLE_PP.S5, single: SINGLE.S5 },
  { code: "S6", from: "2027-07-01", to: "2027-08-31", minStayNights: 1, double: DOUBLE_PP.S6, single: SINGLE.S6 },
  { code: "S7", from: "2027-09-01", to: "2027-10-31", minStayNights: 1, double: DOUBLE_PP.S7, single: SINGLE.S7 },
];

export const ROYAL_ZANZIBAR_DATE_SUPPLEMENTS = [
  {
    id: "nye-gala",
    from: "2026-12-31",
    to: "2026-12-31",
    adult: 133,
    child: 66.5,
    perNight: true,
    mandatory: true,
    label: "New Year's Gala 31 Dec 2026",
  },
  {
    id: "easter",
    from: "2027-03-26",
    to: "2027-03-29",
    adult: 48,
    child: 24,
    perNight: true,
    mandatory: true,
    label: "Easter 26–29 Mar 2027",
  },
];

function parseIso(value) {
  return dayjs(String(value || "").slice(0, 10), "YYYY-MM-DD", true);
}

export function resolveRoyalZanzibarSeasonForDate(iso) {
  const d = parseIso(iso);
  if (!d.isValid()) return null;
  const key = d.format("YYYY-MM-DD");
  for (const season of ROYAL_ZANZIBAR_SEASONS) {
    if (key >= season.from && key <= season.to) return season.code;
  }
  return null;
}

function seasonalMap(field) {
  const prices = {};
  for (const season of ROYAL_ZANZIBAR_SEASONS) {
    prices[season.code] = season[field];
  }
  return prices;
}

const occupancyPricing = {
  pricingKind: "base",
  chargeType: "per_adult",
  childRatePercent: ROYAL_ZANZIBAR_CHILD_SHARING_PERCENT,
  childOwnRoomRatePercent: ROYAL_ZANZIBAR_CHILD_OWN_ROOM_PERCENT,
  extraAdultRatePercent: ROYAL_ZANZIBAR_EXTRA_ADULT_PERCENT,
  childAgeFrom: ROYAL_ZANZIBAR_CHILD_AGE_FROM,
  childAgeTo: ROYAL_ZANZIBAR_CHILD_AGE_TO,
  seasonalPrices: seasonalMap("double"),
  seasonalSinglePrices: seasonalMap("single"),
  onRequest: true,
  fullPrepayment: true,
  allotment: 0,
  board: ROYAL_ZANZIBAR_BOARD,
  boardHe: "הכל כלול",
  infantsAllowed: true,
};

export const royalZanzibarBeachResortContract = {
  hotelCode: ROYAL_ZANZIBAR_CAMPAIGN_SLUG,
  hotelName: "Royal Zanzibar Beach Resort",
  hotelNameHe: "Royal Zanzibar Beach Resort",
  location: "Nungwi, Zanzibar",
  locationHe: "נונגווי, זנזיבר",
  city: "Zanzibar",
  country: "Tanzania",
  starRating: 4,
  kosher: false,
  board: "All Inclusive",
  boardHe: "הכל כלול",
  seasonLabel: "STO 2026 / 2027",
  currency: ROYAL_ZANZIBAR_CURRENCY,
  publicListMarkupPercent: 30,
  publicEffectiveMarkupTargetPercent: 20,
  publicSaleDiscountPercent: 10,
  agentMarkupPercent: 10,
  defaultMarkupPercent: 25,
  minStayNights: 1,
  releaseNights: 1,
  accommodationTaxPerRoomPerNight: 0,
  extraAdultRatePercent: ROYAL_ZANZIBAR_EXTRA_ADULT_PERCENT,
  onRequest: true,
  fullPrepayment: true,
  outOfSeasonMessage:
    "חלק מתאריכי השהייה מחוץ לעונת החוזה של Royal Zanzibar (01/11/2026–31/10/2027).",
  resolveSeasonForDate: resolveRoyalZanzibarSeasonForDate,
  dateSupplements: ROYAL_ZANZIBAR_DATE_SUPPLEMENTS,
  seasons: ROYAL_ZANZIBAR_SEASONS.map(({ code, from, to, minStayNights }) => ({
    code,
    from,
    to,
    minStayNights,
    releaseNights: 1,
  })),
  roomTypes: [
    {
      ...occupancyPricing,
      code: ROYAL_ZANZIBAR_ROOM_CODES.superior,
      id: ROYAL_ZANZIBAR_ROOM_IDS.superior,
      name: "Superior Room",
      nameHe: "Superior Room",
      minAdults: 1,
      maxAdults: 3,
      maxChildren: 1,
      minOccupancy: 1,
      maxOccupancy: 3,
      occupancyLabel: "עד 3 אורחים (יחיד / זוגי / 3 מבוגרים או 2+ילד)",
    },
    {
      ...occupancyPricing,
      pricingKind: "supplement",
      code: ROYAL_ZANZIBAR_ROOM_CODES.superiorDeluxe,
      id: ROYAL_ZANZIBAR_ROOM_IDS.superiorDeluxe,
      name: "Superior Deluxe Room",
      nameHe: "Superior Deluxe Room",
      baseRoomCode: ROYAL_ZANZIBAR_ROOM_CODES.superior,
      baseRoomId: ROYAL_ZANZIBAR_ROOM_IDS.superior,
      supplementAdultPerNight: ROYAL_ZANZIBAR_DELUXE_ADULT_SUPPLEMENT,
      supplementChildPerNight: ROYAL_ZANZIBAR_DELUXE_CHILD_SUPPLEMENT,
      minAdults: 1,
      maxAdults: 3,
      maxChildren: 2,
      minOccupancy: 1,
      maxOccupancy: 4,
      occupancyLabel: "עד 4 אורחים (2+2 ילדים / 3 מבוגרים / 3+ילד)",
    },
    {
      ...occupancyPricing,
      pricingKind: "supplement",
      code: ROYAL_ZANZIBAR_ROOM_CODES.oceanFront,
      id: ROYAL_ZANZIBAR_ROOM_IDS.oceanFront,
      name: "Ocean Front Suite",
      nameHe: "Ocean Front Suite",
      baseRoomCode: ROYAL_ZANZIBAR_ROOM_CODES.superior,
      baseRoomId: ROYAL_ZANZIBAR_ROOM_IDS.superior,
      supplementAdultPerNight: ROYAL_ZANZIBAR_OCEAN_ADULT_SUPPLEMENT,
      supplementChildPerNight: ROYAL_ZANZIBAR_OCEAN_CHILD_SUPPLEMENT,
      minAdults: 1,
      maxAdults: 3,
      maxChildren: 1,
      minOccupancy: 1,
      maxOccupancy: 3,
      occupancyLabel: "עד 3 אורחים (2+ילד / 3 מבוגרים)",
    },
  ],
  remarks: [
    "כל החדרים על בסיס בקשה (All rooms on request).",
    "מחירים נטו, ללא עמלה, כוללים מיסים ממשלתיים קיימים.",
    "תשלום מלא מראש.",
    "ילדים 2–12: 50% בחדר ההורים, 80% בחדר עצמאי. מבוגר שלישי: 70%.",
  ],
  cancellationPolicy: [
    "ביטול מעל 30 ימים לפני הגעה: ללא חיוב",
    "ביטול 20–29 ימים: 25%",
    "ביטול 10–19 ימים: 50%",
    "ביטול 0–9 ימים: 100%",
    "No Show: 100%",
  ],
  paymentPolicy: "תשלום מלא מראש. מחירים נטו שאינם ניתנים לעמלה, כולל מיסים ממשלתיים קיימים.",
  disclaimerHe: "הזמינות על בסיס בקשה — המחיר כפוף לאישור המלון. אין הקצאה אוטומטית.",
};

export function buildRoyalZanzibarCatalogPersistPayload() {
  const c = royalZanzibarBeachResortContract;
  return {
    campaign_code: c.hotelCode,
    slug: c.hotelCode,
    display_name: c.hotelName,
    display_name_he: c.hotelNameHe,
    city: c.city,
    country: c.country,
    destination_codes: ["ZNZ"],
    destination_name_hints: ["zanzibar", "nungwi", "tanzania", "זנזיבר", "נונגווי", "טנזניה"],
    gimmonix_hotel_id: null,
    currency: "USD",
    kosher: false,
    star_rating: c.starRating,
    agent_markup_percent: c.agentMarkupPercent,
    public_markup_percent: c.publicEffectiveMarkupTargetPercent,
    hr_commission_percent: 0,
    min_stay_nights: 1,
    on_request: true,
    full_prepayment: true,
    extra_adult_rate_percent: ROYAL_ZANZIBAR_EXTRA_ADULT_PERCENT,
    date_supplements: ROYAL_ZANZIBAR_DATE_SUPPLEMENTS,
    cancellation_policy: c.cancellationPolicy,
    payment_policy: c.paymentPolicy,
    seasons: c.seasons,
    blackouts: [],
    rooms: c.roomTypes.map((room) => ({
      campaign_code: c.hotelCode,
      room_code: room.code,
      id: room.id,
      name: room.name,
      name_he: room.nameHe,
      board: room.board,
      board_he: room.boardHe,
      allotment: 0,
      charge_type: room.chargeType,
      min_adults: room.minAdults,
      max_adults: room.maxAdults,
      max_children: room.maxChildren,
      min_occupancy: room.minOccupancy,
      max_occupancy: room.maxOccupancy,
      seasonal_prices: room.seasonalPrices,
      seasonal_single_prices: room.seasonalSinglePrices || {},
      currency: "USD",
      pricing_kind: room.pricingKind,
      child_rate_percent: room.childRatePercent,
      child_own_room_rate_percent: room.childOwnRoomRatePercent,
      extra_adult_rate_percent: room.extraAdultRatePercent,
      child_age_from: room.childAgeFrom,
      child_age_to: room.childAgeTo,
      base_room_code: room.baseRoomCode ?? null,
      base_room_id: room.baseRoomId ?? null,
      supplement_adult_per_night: room.supplementAdultPerNight ?? null,
      supplement_child_per_night: room.supplementChildPerNight ?? null,
      on_request: true,
      full_prepayment: true,
    })),
  };
}
